import * as React from 'react';
import styled from 'styled-components';
import { CalculatedField, CalculatedFieldTarget, getObjectiveSources, ObjectiveSource } from '../../../atoms/calculatedFields';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import Popup from '../../../components_v2/popup/Popup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { BorderColor, GreySidely4, RedSidely, SidelyBlack } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { FlexDiv } from '../../products/style';

/// What the pass reads: the report the goals were written on, the column they
/// sit under, and which period a running completion is measured against. The
/// granularity is not part of it — the goals are read at the ventilation of the
/// report, which is where they were keyed in.
type Period = 'whole' | 'prorated' | 'last_complete';
type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'fourMonths' | 'year';
type ObjectiveField = { report: number, column: string, period: Period };

const GRANULARITIES: Granularity[] = ['year', 'fourMonths', 'quarter', 'month', 'week', 'day'];

const GRANULARITY_LABELS: Record<Granularity, string> = {
	day: 'day',
	week: 'week',
	month: 'month',
	quarter: 'quarter',
	fourMonths: 'quadrimester',
	year: 'year',
};

const Title = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 18px 20px;
	border-bottom: 1px solid ${BorderColor};
	color: ${SidelyBlack};
	font-size: 15px;
	font-weight: 600;
`;

const Body = styled.div`
	display: flex;
	flex-flow: column;
	gap: 16px;
	padding: 20px;
	overflow-y: auto;
	height: calc(100vh - 75px);
`;

const Section = styled.h5`
	margin: 0;
	font-size: 12px;
	font-weight: 500;
	color: ${GreySidely4};
`;

const Hint = styled.p`
	margin: 0;
	font-size: 11px;
	color: ${GreySidely4};
`;

const Warn = styled.p`
	margin: 0;
	font-size: 11px;
	color: ${RedSidely};
`;

const nameStyle = { height: '36px', width: '100%', padding: '0 0 0 10px', borderRadius: '5px' };
const wideDropdown = { containerHeight: '36px', optionWidth: '320px', containerWidth: '320px' };

/// Goals written through the report screen store the granularity the way the
/// report names it; goals written through the import store it the way SQL
/// spells it. The same six periods, two spellings.
function readGranularity(stored: string): Granularity | undefined {
	const camel = stored.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
	return GRANULARITIES.find(granularity => granularity === camel);
}

/// What one column of one report holds, as the editor needs to say it: how
/// many goals were written there, at which granularities, and how many of them
/// the pass will be able to attribute to a client company.
type ColumnSummary = {
	name: string,
	goals: number,
	granularities: Record<string, number>,
	perCompany: number,
};

/// The server counts per column and granularity; the editor reads per column.
function summarize(source: ObjectiveSource | undefined): ColumnSummary[] {
	const columns = new Map<string, ColumnSummary>();
	(source?.columns ?? []).forEach(entry => {
		const summary = columns.get(entry.name) ?? { name: entry.name, goals: 0, granularities: {}, perCompany: 0 };
		summary.goals += entry.goals;
		summary.perCompany += entry.per_company;
		const granularity = readGranularity(entry.granularity);
		if (granularity) summary.granularities[granularity] = (summary.granularities[granularity] ?? 0) + entry.goals;
		columns.set(entry.name, summary);
	});
	return [...columns.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/// Writes an objective-completion field: the goals a tenant already keys into a
/// report, turned into one stored number per company — what it did against what
/// it was asked to do — so a scorecard can read it like any other value.
export default function ObjectiveEditor(props: {
	isOpen: boolean,
	setIsOpen: (value: boolean) => void,
	setCalculatedField: (value: CalculatedField | null) => void,
	calculatedField: CalculatedField | null,
}): JSX.Element {
	const [name, setName] = React.useState('');
	const [report, setReport] = React.useState<number>();
	const [column, setColumn] = React.useState('');
	const [period, setPeriod] = React.useState<Period>('whole');
	// only the reports holding goals the pass could read: one that carries none
	// would be a field computing nothing, and there is nothing to warn about
	// afterwards that is better than not offering it
	const [sources, setSources] = React.useState<ObjectiveSource[]>([]);
	// the listing reads every goal of the tenant, which on a large account takes
	// long enough that an empty dropdown would read as "no report qualifies"
	const [loadingState, setLoadingState] = React.useState(LoadingStateEnum.LOADING);

	React.useEffect(() => {
		if (!props.isOpen) return;
		const definition = (props.calculatedField?.report as unknown as { objective?: ObjectiveField })?.objective;
		setName(props.calculatedField?.field_name ?? '');
		setReport(definition?.report);
		setColumn(definition?.column ?? '');
		setPeriod(definition?.period ?? 'whole');
		setLoadingState(LoadingStateEnum.LOADING);
		getObjectiveSources()
			.then(found => {
				setSources(found);
				setLoadingState(LoadingStateEnum.LOADED);
			})
			.catch(error => {
				console.error(error);
				setLoadingState(LoadingStateEnum.ERROR);
			});
	}, [props.isOpen, props.calculatedField]);

	const source = sources.find(entry => entry.report === report);
	const columns = React.useMemo(() => summarize(source), [source]);
	const chosen = columns.find(summary => summary.name === column);
	// an existing field can point at a report whose goals have since been
	// deleted or regrouped: say so rather than show an empty dropdown
	const orphan = !!report && sources.length > 0 && !source;
	const loaded = loadingState === LoadingStateEnum.LOADED;
	// the goals are read at the ventilation of the report, and the editor that
	// writes them falls back to months when a report carries none
	const ventilation = readGranularity(source?.ventilation ?? 'month') ?? 'month';
	const atVentilation = chosen?.granularities[ventilation] ?? 0;

	const reportOptions = React.useMemo<DropdownData<number>[]>(
		() => sources.map(entry => ({ label: entry.name, value: entry.report })),
		[sources]
	);
	// the empty name is a real column: a report with a single unnamed panel
	// keys every goal that way, and the pass addresses it by that same name
	const columnOptions = React.useMemo<DropdownData<string>[]>(
		() => columns.map(summary => ({
			label: `${summary.name || translateToString('objective_editor.only_column')} — ${translateToString('objective_editor.goals_counted', [['COUNT', summary.goals.toString()]])}`,
			value: summary.name,
		})),
		[columns]
	);

	/// A column the chosen report does not have is not a choice the editor can
	/// leave standing: it would save a field addressing a panel that is not
	/// there.
	React.useEffect(() => {
		if (columns.length === 0 || columns.some(summary => summary.name === column)) return;
		setColumn(columns[0].name);
	}, [columns]);

	function save() {
		const trimmedName = name.trim();
		if (!trimmedName) return launchToastError(translateToString('objective_editor.name_required'));
		if (!report) return launchToastError(translateToString('objective_editor.report_required'));
		const definition: ObjectiveField = { report, column, period };
		props.setCalculatedField({
			id: props.calculatedField ? props.calculatedField.id : { unsync: Date.now() } as unknown as number,
			target: CalculatedFieldTarget.Objective,
			field_name: trimmedName,
			display_options: props.calculatedField?.display_options,
			report: { objective: definition } as unknown as CalculatedField['report'],
			ishidded: props.calculatedField?.ishidded ?? false,
		});
		props.setIsOpen(false);
	}

	return <Popup isOpen={props.isOpen} hasBackground disableOutClick onClickOut={() => props.setIsOpen(false)}
		popupStyle={{ top: '0', left: '55vw', height: '100vh', width: '45vw', animate: true, boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.06)' }}
		content={<>
			<Title>
				<Translate id={props.calculatedField ? 'objective_editor.edit' : 'objective_editor.create'} />
				<FlexDiv gap='10px'>
					<DefaultButton buttonStyle={ButtonStyle.White} onClick={() => props.setIsOpen(false)}>
						<Translate id='cancel' />
					</DefaultButton>
					<DefaultButton onClick={save}><Translate id='save' /></DefaultButton>
				</FlexDiv>
			</Title>
			<Body>
				<Section><Translate id='name' /></Section>
				<Input inputStyle={nameStyle} name='objective-name' type='text' value={name} onChange={setName} />
				<Hint><Translate id='objective_editor.value_hint' /></Hint>

				<Section><Translate id='objective_editor.report' /></Section>
				<Dropdown<number>
					dropdownStyle={wideDropdown}
					name='objective-report'
					datalist={reportOptions}
					selectedValue={reportOptions.find(option => option.value === report)}
					onChange={value => {
						setReport(value.value);
						setColumn('');
					}}
				/>
				<Hint><Translate id='objective_editor.report_hint' /></Hint>
				{loaded && sources.length === 0 && <Warn><Translate id='objective_editor.no_source' /></Warn>}
				{orphan && <Warn><Translate id='objective_editor.report_gone' /></Warn>}

				<Section><Translate id='objective_editor.column' /></Section>
				<Dropdown<string>
					dropdownStyle={wideDropdown}
					name='objective-column'
					datalist={columnOptions}
					selectedValue={columnOptions.find(option => option.value === column)}
					onChange={value => setColumn(value.value ?? '')}
				/>
				<Hint><Translate id='objective_editor.column_hint' /></Hint>
				{chosen && chosen.perCompany === 0 && <Warn><Translate id='objective_editor.not_per_company' /></Warn>}
				{chosen && chosen.perCompany > 0 && chosen.perCompany < chosen.goals && <Warn>
					{translateToString('objective_editor.partly_per_company', [['COUNT', chosen.perCompany.toString()], ['TOTAL', chosen.goals.toString()]])}
				</Warn>}

				<Section><Translate id='granularity' /></Section>
				{/* not a choice: the goals of a report are keyed at its ventilation
					and the interpretor only shows the ones that match it, so a field
					with a granularity of its own would stop finding goals the day
					someone moved the report from months to quarters */}
				<Hint>{translateToString('objective_editor.ventilation_hint', [['GRANULARITY', translateToString(GRANULARITY_LABELS[ventilation]).toLocaleLowerCase()]])}</Hint>
				{chosen && atVentilation === 0 && <Warn>
					{translateToString('objective_editor.none_at_ventilation', [['GRANULARITY', translateToString(GRANULARITY_LABELS[ventilation]).toLocaleLowerCase()]])}
				</Warn>}

				<Section><Translate id='objective_editor.period' /></Section>
				<Dropdown<Period>
					dropdownStyle={wideDropdown}
					name='objective-period'
					datalist={(['whole', 'prorated', 'last_complete'] as Period[]).map(value => ({ label: translateToString(`objective_editor.period_${value}`), value }))}
					selectedValue={{ label: translateToString(`objective_editor.period_${period}`), value: period }}
					onChange={value => value.value && setPeriod(value.value)}
				/>
				<Hint>{translateToString(`objective_editor.hint_${period}`)}</Hint>
			</Body>
			<ComponentLoader loadingState={loadingState} />
		</>}
	/>;
}
